import React, { useEffect, useRef } from "react"
import * as echarts from "echarts"
import dayjs from "dayjs"

interface DataItem {
  _id: string
  site: string
  farm: string
  time: string // 날짜 (ISO 형식)
  type: "사용(Wh)" | "발전(Wh)" // 데이터 타입
  value: number // 값
}

interface ChartProps {
  currentMonthData: DataItem[] // 금월 데이터
  lastMonthData: DataItem[] // 전월 데이터
  dataKey: "사용(Wh)" | "발전(Wh)" // 표시할 데이터 키
  height?: string
  width?: string
}

const StackedLineChart2: React.FC<ChartProps> = ({
  currentMonthData,
  lastMonthData,
  dataKey,
  height = "400px",
  width = "100%",
}) => {
  const chartRef = useRef<HTMLDivElement>(null)

  // X축 날짜 (1~31일)
  const xAxisDates = Array.from({ length: 31 }, (_, i) => `${i + 1}`)

  // "금월" | "전월" 타입 정의
  type MonthCategory = "금월" | "전월"

  // 색상 맵핑
  const colors: Record<MonthCategory, string> = {
    금월: "#4282EF",
    전월: "#A6A6A6",
  }

  // 날짜별 데이터 그룹화 및 합산
  const groupAndSumDataByDate = (data: DataItem[]) => {
    return data.reduce<Record<string, number>>((acc, cur) => {
      if (cur.type === dataKey) {
        const dateKey = dayjs(cur.time).format("D") // 날짜만 추출 (1, 2, ..., 31)
        acc[dateKey] = (acc[dateKey] || 0) + cur.value // 날짜별 값 합산
      }
      return acc
    }, {})
  }

  useEffect(() => {
    if (chartRef.current) {
      const myChart = echarts.init(chartRef.current)

      // 데이터 변환
      const currentMonthGrouped = groupAndSumDataByDate(currentMonthData)
      const lastMonthGrouped = groupAndSumDataByDate(lastMonthData)

      const currentMonthValues = xAxisDates.map((date) =>
        Number((currentMonthGrouped[date] || 0).toFixed(0))
      )
      const lastMonthValues = xAxisDates.map((date) =>
        Number((lastMonthGrouped[date] || 0).toFixed(0))
      )

      // Series 데이터 설정
      const series: echarts.SeriesOption[] = (
        ["금월", "전월"] as MonthCategory[]
      ).map((category) => ({
        name: category,
        type: "line",
        smooth: true,
        data: category === "금월" ? currentMonthValues : lastMonthValues,
        lineStyle: {
          color: colors[category], // 라인 색상
          width: 2,
        },
        itemStyle: {
          color: colors[category], // Legend 색상 고정
        },
        areaStyle: {
          opacity: category === "금월" ? 0.8 : 0.3,
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: category === "금월" ? "#4282EF" : "#C6C6C6" },
              { offset: 1, color: category === "금월" ? "#B7CCF7" : "#E3E3E3" },
            ],
          },
        },
        symbol: "none",
        emphasis: {
          focus: "series",
        },
      }))

      // ECharts 옵션
      const option: echarts.EChartsOption = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "line" },
        },
        legend: {
          data: ["금월", "전월"],
          textStyle: { color: "#333" },
        },
        grid: {
          left: "3%",
          right: "4%",
          top: "10%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: xAxisDates,
        },
        yAxis: {
          type: "value",
        },
        series,
      }

      myChart.setOption(option)

      const handleResize = () => {
        myChart.resize()
      }
      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
        myChart.dispose()
      }
    }
  }, [currentMonthData, lastMonthData, dataKey])

  return <div ref={chartRef} style={{ height, width }} />
}

export default StackedLineChart2
